import { useClerk } from "@clerk/react";
import { LockIcon, UsersIcon } from "lucide-react";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { useI18n } from "~/i18n";
import { SettingsRow, SettingsSection } from "./settingsLayout";

/**
 * Account controls in our own Settings surface. This replaces Clerk's default
 * floating menu (Manage account / Sign out) from the sidebar avatar with rows
 * styled like the rest of Settings. Portrait, title, and role live in Clerk's
 * Manage account modal; "Manage" opens that surface for email, password, and
 * security. "Sign out" calls `signOut` — but the trigger, placement, and
 * styling are ours.
 *
 * Renders nothing on non-Clerk desktop / self-hosted builds (no account to
 * manage) or while signed out, so it is safe to drop into the shared General
 * settings panel unconditionally.
 */
export function AccountSettingsSection() {
  if (!hasClerkPublishableKey()) return null;
  return <ConfiguredAccountSettingsSection />;
}

function ConfiguredAccountSettingsSection() {
  const { t } = useI18n();
  const clerk = useClerk();
  const { isSignedIn } = useMorrowClerkAuth();

  if (!isSignedIn) return null;

  return (
    <SettingsSection id="account" title={t("settings.section.account")}>
      <SettingsRow
        title={t("settings.account.manageTitle")}
        description={t("settings.account.manageDescription")}
        control={
          <Button size="xs" variant="outline" onClick={() => void clerk.openUserProfile()}>
            <UsersIcon />
            {t("settings.account.manageAction")}
          </Button>
        }
      />
      <SettingsRow
        title={t("settings.account.signOutTitle")}
        description={t("settings.account.signOutDescription")}
        control={
          <Button size="xs" variant="destructive-outline" onClick={() => void clerk.signOut()}>
            <LockIcon />
            {t("settings.account.signOutAction")}
          </Button>
        }
      />
    </SettingsSection>
  );
}
